import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store/index";
import ApiChildren from "@/api/ApiChildren";
import {
  ChildInfoData,
  ChildInfoResponse,
  ChildResponse,
} from "chaild-api/lib";

/**
 * Kana fields (three keys) are hidden on the EN UI — see ChildInfoPage.
 * - On save, those keys are not in `dataToUpdate`, so we attach each row using
 *   metadata from `doc` but always set `value` to DEFAULT_KANA_VALUE — one katakana
 *   placeholder so backend validation accepts the payload (similar to staffEditModule).
 * - If a kana field is present in `edited` (e.g. JP UI showed the field), we keep that entry as-is.
 * - No edits at all: `saveUpdates` skips the API when `dataToUpdate` is empty.
 */
const DEFAULT_KANA_VALUE = "ア"; // single katakana (U+30A2)

const KANA_KEYS = [
  "addressKana",
  "medicalCenterAddressKana",
  "medicalCenterKana",
] as const;

function flatDocFields(doc: ChildInfoResponse | null): ChildInfoData[] {
  if (!doc) return [];
  return [...doc.basicInfo, ...doc.lifestyleInfo, ...doc.healthInfo];
}

/** API payload = edited fields plus kana rows (format from doc, value always default when omitted from edited). */
function buildSavePayload(
  doc: ChildInfoResponse | null,
  edited: ChildInfoData[]
): ChildInfoData[] {
  const out = new Map(edited.map((d) => [d.key, d]));

  for (const kanaKey of KANA_KEYS) {
    if (out.has(kanaKey)) continue;
    const row = flatDocFields(doc).find((e) => e.key === kanaKey);
    if (!row) continue;
    out.set(kanaKey, {
      ...row,
      value: DEFAULT_KANA_VALUE,
    });
  }

  return Array.from(out.values());
}

export interface ChildDocumentState {
  childId: number | null;
  doc: ChildInfoResponse | null;
  child: ChildResponse | null;

  dataToUpdate: ChildInfoData[];
}

@(Module({ dynamic: true, store, name: "children-document", namespaced: true }) as unknown as (target: new (...args: unknown[]) => VuexModule) => void)
class ChildDocumentModule extends VuexModule implements ChildDocumentState {
  public doc: ChildInfoResponse | null = null;
  public child: ChildResponse | null = null;
  public childId: number | null = null;
  public dataToUpdate: ChildInfoData[] = [];

  @Mutation
  public setChildId(childId: number | null) {
    this.childId = childId;
  }

  @Action({ rawError: true })
  public async getChildInfo() {
    if (this.childId) {
      const response = await ApiChildren.getChildInfo(this.childId);
      if (response) {
        this.setDoc(response);
      }
    }
  }

  @Action({ rawError: true })
  public async getChild() {
    if (this.childId) {
      const response = await ApiChildren.getChild(this.childId);
      if (response) {
        this.setChild(response);
      }
    }
  }

  @Action({ rawError: true })
  public async saveUpdates() {
    if (this.childId && this.dataToUpdate.length > 0) {
      const payload = buildSavePayload(this.doc, this.dataToUpdate);
      const response = await ApiChildren.updateChildInfo(
        this.childId,
        payload
      );
      if (response) {
        this.setDoc(response);
      }
    }
  }

  @Mutation
  public setDoc(doc: ChildInfoResponse) {
    this.doc = doc;
  }

  @Mutation
  public setChild(child: ChildResponse) {
    this.child = child;
  }

  @Mutation
  public updateBasicInfo(value: ChildInfoData) {
    if (this.doc) {
      this.doc.basicInfo = this.doc.basicInfo.map((info) => {
        if (info.key == value.key) {
          return value;
        }
        return info;
      });
    }
  }

  @Mutation
  public updateLifestyleInfo(value: ChildInfoData) {
    if (this.doc) {
      this.doc.lifestyleInfo = this.doc.lifestyleInfo.map((info) => {
        if (info.key == value.key) {
          return value;
        }
        return info;
      });
    }
  }

  @Mutation
  public updateHealthInfo(value: ChildInfoData) {
    if (this.doc) {
      this.doc.healthInfo = this.doc.healthInfo.map((info) => {
        if (info.key == value.key) {
          return value;
        }
        return info;
      });
    }
  }

  @Mutation
  updateDataToUpdate(value: ChildInfoData) {
    let shouldPush = true;
    this.dataToUpdate = this.dataToUpdate.map((d) => {
      if (d.key === value.key) {
        shouldPush = false;
        return value;
      }
      return d;
    });

    if (shouldPush) {
      this.dataToUpdate.push(value);
    }
  }

  @Mutation
  clearLocalData() {
    this.dataToUpdate = [];
    this.child = null;
    this.childId = null;
    this.doc = null;
  }
}

export const childDocumentModule = getModule(ChildDocumentModule);
